import { atom, computed, map, onSet } from "nanostores";

import {
  getGraphDetails,
  GraphDetails,
  GraphSummary,
  updateGraph,
} from "../../api";
import { mergeGraphContent } from "../../services/graph/merge-graph-content.ts";
import { cleanupGraphResources } from "../../services/graph/update-graph.ts";
import { createWorkflowPolling } from "../../services/workflows/create-workflow-polling.ts";
import { $cytoscapeInstance } from "../../stores/graph/ui.ts";
import { createApiStore } from "../api-store";
import { $router } from "../router";

export type StoreReadyState = {
  graphContent: boolean; // Track if initial graph data is ready
};

// Initial content store - handles loading graph data from API
export const $initialGraphContent = createApiStore(getGraphDetails, {
  mapToOptions: ([id]) => ({
    path: { id: id || "" },
  }),
  // Only load graph content when on a graph route
  params: [
    computed($router, (route) =>
      route?.route === "graph" ? route.params.id : null,
    ),
  ],
  storeKey: "graphContent",
});

// Synced content store - contains latest graph state from polling updates
export const $syncedGraphContent = atom<GraphDetails | undefined>(undefined);

// Layout format store - can be updated optimistically when user changes layout
export const $graphLayoutFormat = atom<GraphSummary["format"]>("fcose");

// Internal state tracking to coordinate store readiness
const $storeReadyState = map<StoreReadyState>({
  graphContent: false,
});

// Track the current graphId to clean up when switching away
let currentGraphId: null | string = null;

// Clean reset of all stores when switching graphs
const resetStores = (): void => {
  // Clean up resources for the previous graph before resetting
  const previousGraphId = currentGraphId;

  // Update currentGraphId to the new route's graphId (or null)
  const newRoute = $router.get();
  currentGraphId = newRoute?.route === "graph" ? newRoute.params.id : null;

  $initialGraphContent.set({
    data: undefined,
    error: null,
    loading: false,
  });

  $syncedGraphContent.set(undefined);

  const cyInstance = $cytoscapeInstance.get();
  if (cyInstance) {
    cyInstance.destroy();
  }

  const polling = $workflowPolling.get();
  if (polling) {
    polling.stop();
  }
  $workflowPolling.set(null);

  $graphLayoutFormat.set("fcose");

  $storeReadyState.set({
    graphContent: false,
  });

  // Clean up resources for the previous graph to prevent memory leaks
  if (previousGraphId) {
    void cleanupGraphResources(previousGraphId);
  }
};

// Checks if all required data is ready to begin polling
const shouldStartPolling = (state: StoreReadyState): boolean =>
  state.graphContent;

// Store Subscriptions
// Initialize synced content when initial content loads
onSet($initialGraphContent, ({ newValue }) => {
  // Always update synced content when new data arrives (for refreshes)
  if (newValue.data != null) {
    $syncedGraphContent.set(newValue.data);
    // Only set ready state if not already set (to avoid resetting during refresh)
    if ($storeReadyState.get().graphContent === false) {
      $storeReadyState.setKey("graphContent", true);
    }
  }
  // Sync layout format from initial content
  if (newValue.data?.layout) {
    $graphLayoutFormat.set(newValue.data.layout);
  } else if (newValue.error || !newValue.data) {
    $graphLayoutFormat.set("fcose");
  }
});

// Note: pendingWorkflows tracking removed - polling now starts when graph is ready

const $workflowPolling = atom<{
  getStatus: () => boolean;
  start: () => void;
  stop: () => void;
} | null>(null);

// Start polling when all required data is ready
onSet($storeReadyState, ({ newValue }) => {
  if (shouldStartPolling(newValue)) {
    if ($workflowPolling.get() != null) {
      $workflowPolling.get()?.start();
    } else {
      $workflowPolling.set(createWorkflowPolling());
      $workflowPolling.get()?.start();
    }
  }
});

// Reset all stores when route changes to prevent stale data
$router.subscribe(() => {
  resetStores();
});

// Action to update graph content with merge
export const updateGraphContent = (newContent: GraphDetails): void => {
  const currentGraph = $syncedGraphContent.get();
  if (currentGraph) {
    $syncedGraphContent.set(mergeGraphContent(currentGraph, newContent));
  } else {
    $syncedGraphContent.set(newContent);
  }
};

// Try to update the graph layout format; this may fail for agentic graphs but that's OK
// Fire-and-forget: updates local store optimistically, then sends to server
export const updateGraphLayoutFormat = async (
  graphId: string,
  format: GraphSummary["format"],
): Promise<boolean> => {
  // Update local store immediately for UI feedback
  $graphLayoutFormat.set(format);

  try {
    const result = await updateGraph({
      body: {
        format: format ?? undefined,
      },
      path: { id: graphId },
    });

    if (result.data) {
      return true;
    }
    return false;
  } catch (error) {
    // Handle errors gracefully - log but don't throw
    console.warn("Failed to update graph format (non-critical):", error);
    return false;
  }
};
